import { Search, ArrowUpDown } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * Shared list-control kit: FilterChips (with counts), SearchField
 * (search-as-you-type), and Sort. Used across every portal list so
 * filtering/searching/sorting looks and behaves identically.
 */

export interface FilterChip {
  id: string;
  label: string;
  count?: number;
}

export interface FilterChipsProps {
  chips: FilterChip[];
  activeId: string;
  onChange: (id: string) => void;
  label?: string;
  className?: string;
}

export function FilterChips({ chips, activeId, onChange, label = "Filter", className }: FilterChipsProps) {
  return (
    <div className={cn("flex flex-wrap gap-space-2", className)} role="group" aria-label={label}>
      {chips.map((chip) => {
        const active = chip.id === activeId;
        return (
          <button
            key={chip.id}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(chip.id)}
            className={cn(
              "inline-flex items-center gap-space-1 rounded-pill border px-space-3 py-[5px] text-caption font-medium transition-colors duration-fast ease-standard",
              "focus-visible:outline-none focus-visible:shadow-goldRing",
              active
                ? "border-gold bg-gold text-onyx"
                : "border-border-light bg-white-pure text-gray-mid hover:border-gold hover:text-onyx",
            )}
          >
            {chip.label}
            {chip.count != null && (
              <span
                className={cn(
                  "rounded-pill px-space-1 text-[10px] font-bold tabular-nums",
                  active ? "bg-onyx/10 text-onyx" : "bg-white-warm text-gray-mid",
                )}
              >
                {chip.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  className?: string;
}

export function SearchField({
  value,
  onChange,
  placeholder = "Search…",
  label = "Search",
  className,
}: SearchFieldProps) {
  return (
    <div className={cn("relative", className)}>
      <Search
        size={16}
        aria-hidden
        className="pointer-events-none absolute left-space-3 top-1/2 -translate-y-1/2 text-gray-mid"
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={label}
        className="h-10 w-full rounded-md border border-border-light bg-white-pure pl-[36px] pr-space-3 text-small text-onyx placeholder:text-gray-mid focus-visible:outline-none focus-visible:shadow-goldRing"
      />
    </div>
  );
}

export interface SortOption {
  value: string;
  label: string;
}

export interface SortSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SortOption[];
  label?: string;
  className?: string;
}

export function SortSelect({
  value,
  onChange,
  options,
  label = "Sort by",
  className,
}: SortSelectProps) {
  return (
    <label className={cn("relative inline-flex items-center", className)}>
      <span className="sr-only">{label}</span>
      <ArrowUpDown
        size={14}
        aria-hidden
        className="pointer-events-none absolute left-space-2 top-1/2 -translate-y-1/2 text-gray-mid"
      />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 appearance-none rounded-md border border-border-light bg-white-pure pl-[30px] pr-space-4 text-small text-onyx focus-visible:outline-none focus-visible:shadow-goldRing"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}
